import { Box, Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Confirm } from 'src/components/common/Confirm';
import Loader from 'src/components/common/Loader';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { useUser } from 'src/components/hooks/useUser';
import { getEventValidationReset } from 'src/helpers/events';
import { hideLoader, showLoader } from 'src/store/Loader';
import { setMessage } from 'src/store/Message';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { CalendarEvent, CommonEventValidation, CreateEvent, EditEvent } from 'src/types/Events';
import '../../SubjectTracking.scss';
import { useClenderEvents } from '../useClenderEvents';
import EventsForm from './EventsForm';
import { archLevel } from 'src/data/config';

export function isNew(obj: CreateEvent | EditEvent): boolean {
  return !(obj.id !== '');
}

interface ScheduleEventDialogProps {
  isDialogOpen: boolean;
  event: CreateEvent | EditEvent;
  calendarEvents: CalendarEvent[];
  visitTypesList: any;
  subVisitTypesList: any;
  onSuccess(): void;
  onClose(): void;
}

export default function ScheduleEventDialog(props: ScheduleEventDialogProps) {
  const { t } = useTranslation();
  const { user: currentUserName } = useUser();
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const dispatch = useAppDispatch();
  const [event, setEvent] = useState<CreateEvent | EditEvent>(props.event);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const isNewEvent = isNew(event);
  const { createMonitoringEvent, updateMonitoringEvent } = useClenderEvents();
  const [validationObj, setValidationObj] = useState<CommonEventValidation>(getEventValidationReset());
  useEffect(() => {
    setEvent(props.event);
  }, [props.event]);

  const isMandatoryFieldsSupplied = (eventToSave: CreateEvent | EditEvent): boolean => {
    const tempArr = [];
    let tempValidArray = validationObj;
    if (eventToSave.visit_type === '') {
      tempValidArray = { ...tempValidArray, visit_type: true };
      tempArr.push(true);
    }
    if (eventToSave.start_date === '') {
      tempValidArray = { ...tempValidArray, start_date: true };
      tempArr.push(true);
    }
    if (eventToSave.end_date === '') {
      tempValidArray = { ...tempValidArray, end_date: true };
      tempArr.push(true);
    }
    if (eventToSave.sub_type === '') {
      tempValidArray = { ...tempValidArray, sub_type: true };
      tempArr.push(true);
    }

    setValidationObj(tempValidArray);
    if (tempArr.length > 0) {
      return !tempArr.some(e => e);
    } else {
      return true;
    }
  };

  const checkForSameCreateEvent = (eventToSave: CreateEvent | EditEvent): any => {
    const isSameEventWithMatchingSubType = props.calendarEvents.some(
      (singleEvent: any) =>
        eventToSave.visit_type === singleEvent.visit_type &&
        eventToSave.sub_type === singleEvent.sub_type && //with matching subtype
        eventToSave.start_date === singleEvent.start_date &&
        eventToSave.end_date === singleEvent.end_date
    );

    const isSameEventWithoutMatchingSubType = props.calendarEvents.some(
      (singleEvent: any) =>
        eventToSave.visit_type === singleEvent.visit_type &&
        eventToSave.start_date === singleEvent.start_date && //without matching subtype
        eventToSave.end_date === singleEvent.end_date
    );
    return {
      matchingSubType: isSameEventWithMatchingSubType,
      withoutMatchingSubType: isSameEventWithoutMatchingSubType
    };
  };
  const checkForSameEditEvent = (eventToSave: CreateEvent | EditEvent): any => {
    const isSameEvent = props.calendarEvents.some(
      (singleEvent: any) =>
        eventToSave.visit_type === singleEvent.visit_type &&
        eventToSave.sub_type === singleEvent.sub_type &&
        eventToSave.start_date === singleEvent.start_date &&
        eventToSave.end_date === singleEvent.end_date &&
        eventToSave.id === singleEvent.id //with matching id
    );
    const isSameEventWithMatchingSubType = props.calendarEvents.some(
      (singleEvent: any) =>
        eventToSave.visit_type === singleEvent.visit_type &&
        eventToSave.sub_type === singleEvent.sub_type && //with matching subtype
        eventToSave.start_date === singleEvent.start_date &&
        eventToSave.end_date === singleEvent.end_date &&
        eventToSave.id !== singleEvent.id
    );
    const isSameEventWithoutMatchingSubType = props.calendarEvents.some(
      (singleEvent: any) =>
        eventToSave.visit_type === singleEvent.visit_type &&
        eventToSave.start_date === singleEvent.start_date && //without matching subtype
        eventToSave.end_date === singleEvent.end_date &&
        eventToSave.id !== singleEvent.id
    );
    return {
      isSameEvent,
      matchingSubType: isSameEventWithMatchingSubType,
      withoutMatchingSubType: isSameEventWithoutMatchingSubType
    };
  };

  const checkForOverlapping = (eventToSave: CreateEvent | EditEvent): boolean => {
    const isOverlapped = props.calendarEvents.map((singleEvent: any) => {
      if (
        eventToSave.visit_type === singleEvent.visit_type &&
        // eventToSave.sub_type === singleEvent.sub_type &&
        eventToSave.id !== singleEvent.id
      ) {
        const currentEventStartDate = moment(eventToSave.start_date);
        const currentEventEndDate = moment(eventToSave.end_date);
        const existingEventStartDate = moment(singleEvent.start_date);
        const existingEventEndDate = moment(singleEvent.end_date);
        if (
          (currentEventStartDate >= existingEventStartDate && currentEventStartDate <= existingEventEndDate) ||
          (currentEventEndDate >= existingEventStartDate && currentEventEndDate <= existingEventEndDate) ||
          (existingEventStartDate >= currentEventStartDate && existingEventStartDate <= currentEventEndDate) ||
          (existingEventEndDate >= currentEventStartDate && existingEventEndDate <= currentEventEndDate)
        ) {
          return true;
        } else {
          return false;
        }
      }
    });

    if (!isOverlapped.includes(true)) {
      return true;
    } else {
      return false;
    }
  };

  const createCalendarEvent = async (eventToSave: CreateEvent | EditEvent) => {
    dispatch(showLoader());
    const createResponse: any = await createMonitoringEvent({
      site_id: selectedSite == null ? '' : selectedSite,
      study_id: studyId,
      country: selectedCountry,
      data: { ...eventToSave, created_by: currentUserName }
    });
    if (createResponse?.data) {
      dispatch(
        setMessage({
          msgDetails: 'Event created sucessfully',
          severity: 'success'
        })
      );
      props.onSuccess();
      props.onClose();
    } else if (createResponse?.error) {
      if (createResponse?.error.status == 500) {
        dispatch(
          setMessage({
            msgDetails: 'Monitoring visit already exists',
            severity: 'error'
          })
        );
      } else if (createResponse?.error.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      }
    }
    dispatch(hideLoader());
  };

  const updateCalendarEvent = async (eventToSave: CreateEvent | EditEvent) => {
    dispatch(showLoader());
    const updateResponse: any = await updateMonitoringEvent({
      id: eventToSave.id,
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: archLevel === 'site' ? process.env.REACT_APP_TSDA_SLUG : process.env.REACT_APP_NAME,
      data: {
        visit_type: eventToSave.visit_type,
        sub_type: eventToSave.sub_type,
        start_date: eventToSave.start_date,
        end_date: eventToSave.end_date,
        updated_by: currentUserName
      }
    });
    if (updateResponse?.data) {
      dispatch(
        setMessage({
          msgDetails: 'Event updated sucessfully',
          severity: 'success'
        })
      );
      props.onSuccess();
      props.onClose();
    } else if (updateResponse?.error) {
      if (updateResponse?.error.status == 500) {
        dispatch(
          setMessage({
            msgDetails: `${updateResponse?.error.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (updateResponse?.error.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      }
    }
    dispatch(hideLoader());
  };
  const saveEvent = async (eventToSave: CreateEvent | EditEvent) => {
    if (isMandatoryFieldsSupplied(eventToSave)) {
      if (eventToSave.start_date === 'Invalid date' || eventToSave.end_date === 'Invalid date') {
        setValidationObj({
          ...validationObj,
          end_date: eventToSave.end_date === 'Invalid date' ? true : false,
          start_date: eventToSave.start_date === 'Invalid date' ? true : false
        });
        dispatch(
          setMessage({
            msgDetails: 'Invalid Date(s)',
            severity: 'error'
          })
        );
      } else if (moment(eventToSave.start_date).isSame(moment(eventToSave.end_date))) {
        dispatch(
          setMessage({
            msgDetails: 'Start date and time should not be equal to end date and time',
            severity: 'error'
          })
        );
      } else if (moment(eventToSave.start_date) > moment(eventToSave.end_date)) {
        dispatch(
          setMessage({
            msgDetails: 'Start date and time must be less than end date and time',
            severity: 'error'
          })
        );
      } else {
        let sameEventValidation: any = {};
        if (isNewEvent) {
          if (
            eventToSave.visit_type === 'Interim Monitoring Visit' ||
            eventToSave.visit_type === 'Site Qualification Visit' ||
            eventToSave.visit_type === 'Site Initiation Visit'
          ) {
            sameEventValidation = checkForSameCreateEvent(eventToSave);
            if (!sameEventValidation.matchingSubType && !sameEventValidation.withoutMatchingSubType) {
              if (checkForOverlapping(eventToSave)) {
                createCalendarEvent(eventToSave);
              } else {
                dispatch(
                  setMessage({
                    msgDetails: 'Events overlapped',
                    severity: 'error'
                  })
                );
              }
            } else {
              displayVisitExistsMessageForCreate(sameEventValidation);
            }
          }
        } else {
          sameEventValidation = checkForSameEditEvent(eventToSave);
          if (
            !sameEventValidation.isSameEvent &&
            !sameEventValidation.matchingSubType &&
            !sameEventValidation.withoutMatchingSubType
          ) {
            if (checkForOverlapping(eventToSave)) {
              updateCalendarEvent(eventToSave);
            } else {
              dispatch(
                setMessage({
                  msgDetails: 'Events overlapped',
                  severity: 'error'
                })
              );
            }
          } else {
            displayVisitExistsMessageForUpdate(sameEventValidation);
          }
        }
      }
    }
    function displayVisitExistsMessageForCreate(eventMatchingObj: any) {
      dispatch(
        setMessage({
          msgDetails: eventMatchingObj.matchingSubType
            ? 'Monitoring visit already exists'
            : eventMatchingObj.withoutMatchingSubType
            ? 'Visit already exists with the same date & time'
            : '',
          severity: 'error'
        })
      );
    }
    function displayVisitExistsMessageForUpdate(eventMatchingObj: any) {
      dispatch(
        setMessage({
          msgDetails: eventMatchingObj.isSameEvent
            ? 'No updates'
            : eventMatchingObj.matchingSubType
            ? 'Record already exists'
            : eventMatchingObj.withoutMatchingSubType
            ? 'Visit already created with the same start date and time, End date and time'
            : 'Checking',
          severity: 'error'
        })
      );
    }
  };

  return (
    <PopupComponent className="popup-component" open={props.isDialogOpen}>
      <Loader />
      <DialogTitle>
        Schedule:{' '}
        {event.start_date !== '' && event.end_date !== ''
          ? `${moment(event.start_date).format('LL')} to ${moment(event.end_date).format('LL')}`
          : event.start_date !== ''
          ? `${moment(event.start_date).format('LL')}`
          : ``}
      </DialogTitle>
      <DialogContent>
        <EventsForm
          event={event}
          setEvent={setEvent}
          visitTypesList={props.visitTypesList}
          subVisitTypesList={props.subVisitTypesList}
          validationObj={validationObj}
          setValidationObj={setValidationObj}
          isNew={isNew}
        />
      </DialogContent>
      <DialogActions>
        <Box flexGrow={1} />
        <br />
        <Button onClick={props.onClose} className="grey-btn">
          {t('Close')}
        </Button>
        <Button onClick={() => saveEvent(event)}>{t('Save')}</Button>
      </DialogActions>
      <Confirm
        isOpen={isConfirmOpen}
        message="Are you sure you want to cancel this event?"
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          setIsConfirmOpen(false);
        }}
      />
    </PopupComponent>
  );
}
